/**
 * @fileoverview Modal de confirmação de ações (destrutivas ou críticas).
 * Linguagem visual alinhada ao modal de Observações do Contato (ContactNotes):
 * radius 18/20, gradiente de CTA, dark-mode nativo, transições refinadas.
 *
 * API retrocompatível:
 * - `confirmLoading` (opt-in): quando passado, parent controla o close — click
 *   em Confirmar NÃO fecha o modal automaticamente; spinner aparece no botão e
 *   ESC/backdrop ficam bloqueados enquanto `true`.
 */

import React from "react";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import { AlertTriangle, Loader2, X } from "lucide-react";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
	const dark = theme.palette.type === "dark";
	const textPrimary = dark ? "#f8fafc" : "#111827";
	const textSecondary = dark ? "rgba(248, 250, 252, 0.72)" : "#4b5563";
	const textMuted = dark ? "rgba(248, 250, 252, 0.5)" : "#9ca3af";
	const border = dark
		? "1px solid rgba(255, 255, 255, 0.12)"
		: "1px solid rgba(15, 23, 42, 0.12)";
	const surface = dark ? "#0f172a" : "#ffffff";
	const backdrop = dark ? "rgba(2, 6, 23, 0.72)" : "rgba(15, 23, 42, 0.48)";
	const warnBg = dark
		? "linear-gradient(135deg, rgba(248, 113, 113, 0.18) 0%, rgba(239, 68, 68, 0.12) 100%)"
		: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)";
	const warnBorder = dark
		? "1px solid rgba(248, 113, 113, 0.22)"
		: "1px solid rgba(244, 63, 94, 0.18)";
	const warnIcon = dark ? "#fda4af" : "#e11d48";
	const t = "all 180ms cubic-bezier(0.4, 0, 0.2, 1)";
	const btnBase = {
		height: 38,
		borderRadius: 10,
		fontSize: "0.95rem",
		fontWeight: 500,
		fontFamily: "inherit",
		textTransform: "none",
		cursor: "pointer",
		transition: t,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	};

	return {
		paper: {
			borderRadius: 20,
			background: surface,
			boxShadow: dark
				? "0 24px 48px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06)"
				: "0 24px 48px -12px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(15, 23, 42, 0.04)",
			overflow: "hidden",
			width: "100%",
			maxWidth: 460,
			animation: "$rise 220ms cubic-bezier(0.4, 0, 0.2, 1)",
		},
		backdrop: {
			backgroundColor: backdrop,
		},
		header: {
			padding: theme.spacing(3, 3, 0, 3),
			display: "flex",
			gap: 14,
			alignItems: "flex-start",
			position: "relative",
		},
		iconBadge: {
			width: 44,
			height: 44,
			borderRadius: 14,
			background: warnBg,
			border: warnBorder,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			color: warnIcon,
			flexShrink: 0,
		},
		headText: {
			flex: 1,
			minWidth: 0,
			paddingTop: 2,
		},
		eyebrow: {
			fontSize: 10,
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.14em",
			color: textMuted,
			marginBottom: 4,
		},
		title: {
			fontSize: 17,
			fontWeight: 700,
			color: textPrimary,
			letterSpacing: "-0.01em",
			lineHeight: 1.35,
			margin: 0,
			wordBreak: "break-word",
		},
		closeBtn: {
			position: "absolute",
			top: 12,
			right: 12,
			width: 30,
			height: 30,
			borderRadius: 10,
			border: "none",
			background: "transparent",
			color: textMuted,
			cursor: "pointer",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			transition: t,
			"&:hover:not(:disabled)": {
				background: dark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.06)",
				color: textPrimary,
			},
			"&:disabled": { opacity: 0.35, cursor: "not-allowed" },
		},
		body: {
			padding: theme.spacing(2, 3, 2.5, 3),
			color: textSecondary,
			fontSize: "0.95rem",
			lineHeight: 1.55,
			"& p": { margin: 0 },
			"& p + p": { marginTop: theme.spacing(1) },
		},
		actions: {
			display: "flex",
			justifyContent: "flex-end",
			gap: 8,
			padding: theme.spacing(2, 3, 2.5, 3),
			borderTop: dark
				? "1px solid rgba(255, 255, 255, 0.06)"
				: "1px solid rgba(15, 23, 42, 0.06)",
			background: dark ? "rgba(255, 255, 255, 0.02)" : "rgba(248, 250, 252, 0.6)",
		},
		btnGhost: {
			...btnBase,
			padding: "0 18px",
			border,
			background: "transparent",
			color: textSecondary,
			"&:hover:not(:disabled)": {
				background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
				color: textPrimary,
			},
			"&:disabled": { opacity: 0.45, cursor: "not-allowed" },
		},
		btnConfirm: {
			...btnBase,
			minWidth: 112,
			padding: "0 20px",
			border: "none",
			background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
			color: "#ffffff",
			fontWeight: 500,
			boxShadow: dark
				? "0 6px 14px -4px rgba(37, 99, 235, 0.45)"
				: "0 6px 14px -4px rgba(59, 130, 246, 0.45)",
			"&:hover:not(:disabled)": {
				background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
				boxShadow: dark
					? "0 8px 18px -4px rgba(37, 99, 235, 0.55)"
					: "0 8px 18px -4px rgba(59, 130, 246, 0.55)",
			},
			"&:disabled": { opacity: 0.55, cursor: "not-allowed", boxShadow: "none" },
		},
		spin: { animation: "$spin 0.8s linear infinite" },
		"@keyframes spin": {
			"0%": { transform: "rotate(0deg)" },
			"100%": { transform: "rotate(360deg)" },
		},
		"@keyframes rise": {
			"0%": { opacity: 0, transform: "translateY(8px) scale(0.98)" },
			"100%": { opacity: 1, transform: "translateY(0) scale(1)" },
		},
	};
});

const ConfirmationModal = ({
	title,
	children,
	open,
	onClose,
	onConfirm,
	confirmLoading,
}) => {
	const classes = useStyles();
	const isLoading = !!confirmLoading;

	const paperProps = React.useMemo(
		() => ({ className: classes.paper, elevation: 0 }),
		[classes.paper]
	);
	const backdropProps = React.useMemo(
		() => ({ className: classes.backdrop }),
		[classes.backdrop]
	);

	// Bloqueia fechamento via ESC/backdrop quando isLoading via reason no
	// onClose (substitui disableEscapeKeyDown/disableBackdropClick deprecated).
	const handleClose = (_event, reason) => {
		if (isLoading) return;
		if (reason === "backdropClick" || reason === "escapeKeyDown") {
			// Permitido fechar — apenas reflete pro parent que o usuário
			// dispensou o modal (false = "não confirmou").
		}
		onClose(false);
	};

	const handleConfirm = () => {
		if (isLoading) return;
		// Parent-controlled close: quando confirmLoading é definido, o parent
		// decide quando fechar (após a ação async resolver).
		if (confirmLoading === undefined) {
			onClose(false);
		}
		onConfirm();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="confirm-dialog-title"
			PaperProps={paperProps}
			BackdropProps={backdropProps}
		>
			<div className={classes.header}>
				<span className={classes.iconBadge} aria-hidden="true">
					<AlertTriangle size={22} strokeWidth={1.8} />
				</span>
				<div className={classes.headText}>
					<div className={classes.eyebrow}>Confirmação</div>
					<h2 id="confirm-dialog-title" className={classes.title}>
						{title}
					</h2>
				</div>
				<button
					type="button"
					className={classes.closeBtn}
					onClick={handleClose}
					disabled={isLoading}
					aria-label={i18n.t("confirmationModal.buttons.cancel")}
				>
					<X size={16} strokeWidth={2} />
				</button>
			</div>

			<div className={classes.body}>
				{typeof children === "string" ? <p>{children}</p> : children}
			</div>

			<div className={classes.actions}>
				<button
					type="button"
					className={classes.btnGhost}
					onClick={handleClose}
					disabled={isLoading}
				>
					{i18n.t("confirmationModal.buttons.cancel")}
				</button>
				<button
					type="button"
					className={classes.btnConfirm}
					onClick={handleConfirm}
					disabled={isLoading}
				>
					{isLoading && (
						<Loader2 size={15} strokeWidth={2} className={classes.spin} />
					)}
					{i18n.t("confirmationModal.buttons.confirm")}
				</button>
			</div>
		</Dialog>
	);
};

export default ConfirmationModal;
